import { useCallback, useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import Swal from "sweetalert2";
import {
  apiRequest,
  formatCurrency,
  formatDate,
  getProfileImage,
} from "../../../core/common/api";
import { canManageUsers } from "../../../core/common/adminPermissions";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import AppImage from "../../../shared/AppImage";
import MetricCard from "../../../shared/MetricCard";
import Tree from "../../../shared/Tree";
import PaginationBar from "../../../shared/PaginationBar";
import ResetButton from "../../../shared/ResetButton";
import HistoryDetails from "../../../shared/HistoryDetails";
import { PROGRESS_SEQUENCE, clubLabel, clubLevel, rankKey, orderStatusTone } from "../../../shared/Utils";

type UserFormState = {
  name: string;
  email: string;
  phone: string;
  password: string;
  isActive: boolean;
};

const registerFromLabel = (value = "") => {
  if (value === "web") return "Website";
  if (value === "app") return "Mobile App";
  return value || "—";
};

const HISTORY_FUNDS = [
  { value: "all", label: "All funds" },
  { value: "ewallet", label: "eWallet" },
  { value: "working", label: "Working" },
  { value: "upline", label: "Up-line" },
  { value: "upgrade", label: "Upgrade" },
  { value: "repurchase", label: "Re-purchases" },
  { value: "profit", label: "Profit" },
];

const copyValue = async (label: string, value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    await Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `${label} copied`,
      showConfirmButton: false,
      timer: 1200,
    });
  } catch {
    Swal.fire("Copy Failed", `Could not copy ${label.toLowerCase()}`, "error");
  }
};

const CLUB_SPLIT_CHIPS = [
  { key: "upgradeFund", label: "Upgrade", tone: "blue" },
  { key: "uplineFund", label: "Upline", tone: "slate" },
  { key: "workingFund", label: "Working", tone: "green" },
  { key: "repurchase", label: "Re-purchase", tone: "orange" },
] as const;

const ClubMilestoneRow = ({ item, status }: { item: any; status: string }) => {
  const perSale = Number(item.profitContribution) || 0;
  const pool = Number(item.totalContribution) || 0;
  const remaining = Number(item.remaining) || 0;

  return (
    <div className={`club-ms ${item.done ? "is-paid" : status === "locked" ? "is-locked" : "is-next"}`}>
      <div className="club-ms-top">
        <strong>{item.sales === 9 ? "9 groups × 3" : "3 placement sales"}</strong>
        <span className={`club-ms-pill ${item.done ? "paid" : status === "locked" ? "lock" : "wait"}`}>
          {item.done ? "Complete" : status === "locked" ? "Locked" : status === "paused" ? "Payout paused" : remaining ? `${remaining} ${item.sales === 9 ? "groups" : "sales"} left` : "Payout pending"}
        </span>
      </div>
      <p className="text-muted-sm">
        {item.doneCount}/{item.sales} {item.sales === 9 ? "complete groups" : "placement sales"}
        {item.sales === 9 && ` · ${item.placementsDone}/${item.placementTarget} third-level placements; each grandchild needs 3`}
      </p>
      <p className="club-ms-formula">
        {formatCurrency(perSale)} × {item.sales} {item.sales === 9 ? "groups" : "sales"} = <b>{formatCurrency(pool)}</b> pool
      </p>
      <div className="club-ms-chips">
        {CLUB_SPLIT_CHIPS.map((chip) => {
          const amount = Number(item[chip.key]) || 0;
          return (
            <span key={chip.key} className={`club-chip tone-${chip.tone} ${amount ? "" : "is-zero"}`}>
              <em>{chip.label}</em>
              {formatCurrency(amount)}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const ClubProgressBoard = ({ progress, ranks, currentClub }: {
  progress: any;
  ranks: any;
  currentClub: number | string;
}) => {
  const [openClub, setOpenClub] = useState<number | string | null>(currentClub);
  useEffect(() => setOpenClub(currentClub), [currentClub]);
  const sequence: Array<number | string> = PROGRESS_SEQUENCE;
  const [progressType, setProgressType] = useState("all");

  return <>
    <div className="club-progress-filter">
      <label className="club-progress-filter-label" htmlFor="club-progress-type">
        View
      </label>
      <select
        id="club-progress-type"
        className="club-progress-filter-select"
        aria-label="Filter progress"
        value={progressType}
        onChange={event => setProgressType(event.target.value)}
      >
        <option value="all">Club and rank progress</option>
        <option value="club">Club progress only</option>
        <option value="rank">Rank progress only</option>
      </select>
    </div>
    <div className="club-board">
    {sequence.map((club, index) => {
      if (progressType === "club" && typeof club !== "number") return null;
      if (progressType === "rank" && typeof club !== "string") return null;
      const numbered = typeof club === "number";
      const details = numbered
        ? progress?.clubs?.find((row: any) => row.club === club)
        : ranks?.requirements?.find((row: any) => row.rank === club);
      const isCurrent = numbered ? club === currentClub : club === ranks?.rank;
      const isOpen = openClub === club;
      const status = !details ? "unavailable" : numbered ? details.status
        : isCurrent ? "current" : details.qualified ? "qualified" : "unqualified";
      const completed = status === "done" || status === "qualified";
      const summary = !details ? "Progress unavailable; reload user details"
        : numbered ? status === "done" ? "Both stages complete"
          : status === "paused" ? "Payouts paused"
          : status === "locked" ? `Opens after ${clubLabel(sequence[index - 1])}`
          : `${details.salesDone}/${details.salesTarget} ${details.salesTarget === 9 ? "complete groups of 3" : "placement sales"}`
        : `${details.qualified ? "Qualified" : "Not qualified"} for ${ranks.month}`;

      return <article key={club} className={`club-card ${isCurrent ? "is-current" : ""} ${isOpen ? "is-open" : ""}`}>
        <button type="button" className="club-card-toggle" aria-expanded={isOpen}
          aria-controls={`club-panel-${club}`} onClick={() => setOpenClub(isOpen ? null : club)}>
          <span className="club-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg>
          </span>
          <span className="club-card-toggle-copy">
            <strong>{details?.name || clubLabel(club)}</strong>
            <small>{summary}</small>
          </span>
          <span className={`status-badge ${completed ? "success" : isCurrent ? "warning" : "muted"}`}>
            {status === "unavailable" ? "Unavailable" : status === "paused" ? "Paused"
              : isCurrent ? "Current" : completed ? (numbered ? "Complete" : "Qualified") : numbered ? "Locked" : "Not qualified"}
          </span>
        </button>
        <div id={`club-panel-${club}`} className="club-card-panel" hidden={!isOpen}>
          {!details ? <p className="club-rank-details text-muted-sm">Progress unavailable; reload user details.</p>
            : numbered ? <div className="club-card-body">
              {details.milestones.map((item: any) => <ClubMilestoneRow key={item.sales} item={item} status={status} />)}
            </div> : <NamedClubDetails item={details} ranks={ranks} />}
        </div>
      </article>;
    })}
  </div></>;
};

const rankNeedRows = (item: any, ranks: any) => {
  const downline = ranks?.downline || {};
  const rows: Array<{ label: string; current: number; target: number }> = [];
  if (item.monthSales) {
    rows.push({
      label: "Sales this month",
      current: Number(ranks?.monthSales) || 0,
      target: Number(item.monthSales) || 0,
    });
  }
  if (item.need?.leader) {
    rows.push({
      label: "Leaders in downline",
      current: Number(downline.leader) || 0,
      target: Number(item.need.leader) || 0,
    });
  }
  if (item.need?.director) {
    rows.push({
      label: "Directors in downline",
      current: Number(downline.director) || 0,
      target: Number(item.need.director) || 0,
    });
  }
  if (item.need?.partner) {
    rows.push({
      label: "Partners in downline",
      current: Number(downline.partner) || 0,
      target: Number(item.need.partner) || 0,
    });
  }
  return rows;
};

const NamedClubDetails = ({ item, ranks }: { item: any; ranks: any }) => {
  const rows = rankNeedRows(item, ranks);
  const key = rankKey(item.rank);
  return (
    <div className="club-rank-details">
      <div className="rank-details-heading">
        <div>
          <span className="rank-kicker">{item.percent}% company profit</span>
          <p className="text-muted-sm">Pool shared among members qualifying for {ranks.month}.</p>
        </div>
        <span className={`status-badge ${item.qualified ? "success" : "muted"}`}>
          {item.qualified ? "Qualified this month" : "Not qualified this month"}
        </span>
      </div>
      {key === "royalty" && <div className="rank-req">
        <p className="text-muted-sm">Initial rank target: {item.targetMonths} months after achieving Partner.</p>
        <p className="text-muted-sm">{ranks.royaltyPreviouslyAchieved
          ? "Initial Royalty target achieved. Monthly requirements still apply."
          : ranks.royaltyDeadline
            ? `${ranks.royaltyWindowOpen ? "Target deadline" : "Target window closed"}: ${formatDate(ranks.royaltyDeadline)}`
            : "Achieve Partner to start the Royalty target window."}</p>
      </div>}
      {rows.map((row) => {
        const percent = Math.min(100, Math.round((row.current / Math.max(row.target, 1)) * 100));
        const met = row.current >= row.target;
        return (
          <div key={row.label} className={`rank-req ${met ? "is-met" : ""}`}>
            <div className="rank-req-meta">
              <span>{row.label}</span>
              <b>
                {row.current}/{row.target}
              </b>
            </div>
            <div className="rank-req-track">
              <div className="rank-req-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
        );
      })}
      {!rows.length && <p className="text-muted-sm">No sales or team targets configured. Monthly eligibility is shown above.</p>}
    </div>
  );
};

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [user, setUser] = useState<any>(null);
  const [saleUser, setSaleUser] = useState<any>(null);
  const [stats, setStats] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [children, setChildren] = useState<any[]>([]);
  const [tree, setTree] = useState<any>(null);
  const [treeTotal, setTreeTotal] = useState(0);
  const [wallet, setWallet] = useState<any>(null);
  const [ranks, setRanks] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({
    type: "all",
    fund: "all",
    from: "",
    to: "",
  });
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<UserFormState>({
    name: "",
    email: "",
    phone: "",
    password: "",
    isActive: true,
  });

  const canEditUser = canManageUsers();

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await apiRequest(`/users/${id}`, "GET");
      if (res.success && res.user) {
        setUser(res.user);
        setSaleUser(res.saleUser || null);
        setForm({
          name: res.user.name || "",
          email: res.user.email || "",
          phone: res.user.phone || "",
          password: "",
          isActive: res.user.isActive !== false,
        });
        setStats(res.stats || {});
        setRecentOrders(res.recentOrders || []);
        setAddresses(res.addresses || []);
        setChildren(res.children || []);
        setWallet(res.wallet || null);
        setRanks(res.ranks || null);
        setIsEditing(false);
        if (res.saleUser?.memberId) {
          try {
            const treeRes = await apiRequest(
              `/sale-users/tree?memberId=${encodeURIComponent(res.saleUser.memberId)}`,
              "GET",
            );
            setTree(treeRes.tree || null);
            setTreeTotal(treeRes.total || 0);
          } catch {
            setTree(null);
            setTreeTotal(0);
          }
        } else {
          setTree(null);
          setTreeTotal(0);
        }
      } else {
        throw new Error(res.message || "User not found");
      }
    } catch (err: any) {
      Swal.fire("Error", err.message || "Failed to load user", "error");
      navigate("/users");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const loadHistory = useCallback(
    async (page = 1) => {
      if (!id) return;
      setHistoryLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(historyPagination.limit),
        });
        if (historyFilter.type !== "all") params.set("type", historyFilter.type);
        if (historyFilter.fund !== "all") params.set("fund", historyFilter.fund);
        if (historyFilter.from) params.set("from", historyFilter.from);
        if (historyFilter.to) params.set("to", historyFilter.to);
        const res = await apiRequest(`/users/${id}/history?${params}`, "GET");
        setHistory(res.history || []);
        setHistoryPagination({
          page: res.pagination?.page || page,
          limit: res.pagination?.limit || historyPagination.limit,
          totalPages: res.pagination?.totalPages || 1,
          total: res.pagination?.total || 0,
        });
      } catch (err: any) {
        Swal.fire("Error", err.message || "Failed to load history", "error");
      } finally {
        setHistoryLoading(false);
      }
    },
    [id, historyFilter, historyPagination.limit],
  );

  useEffect(() => {
    void loadHistory(1);
  }, [loadHistory]);

  useEffect(() => {
    if (searchParams.get("edit") === "1" && canEditUser && user) {
      setIsEditing(true);
    }
  }, [searchParams, canEditUser, user]);

  const handleCancelEdit = () => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        password: "",
        isActive: user.isActive !== false,
      });
    }
    setIsEditing(false);
  };

  const handleSaveUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || !canEditUser) return;

    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        isActive: form.isActive,
      };
      if (form.password.trim()) {
        payload.password = form.password.trim();
      }
      const res = await apiRequest(`/users/${user._id}`, "PUT", payload);

      if (!res.success) throw new Error(res.message || "Unable to update user");

      await Swal.fire({
        title: "User Updated",
        text: "Customer details saved successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      await fetchUser();
    } catch (err: any) {
      Swal.fire(
        "Update Failed",
        err.message || "Could not update user",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!user?.memberId || !canEditUser) return;
    const { value } = await Swal.fire({
      title: "eWallet transfer",
      html:
        '<input id="rw-transfer-to" class="swal2-input" placeholder="Receiver member ID" />' +
        '<input id="rw-transfer-amount" class="swal2-input" type="number" min="1" placeholder="Amount" />',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Transfer",
      preConfirm: () => {
        const toMemberId = (
          document.getElementById("rw-transfer-to") as HTMLInputElement | null
        )?.value?.trim();
        const amount = Number(
          (document.getElementById("rw-transfer-amount") as HTMLInputElement | null)
            ?.value,
        );
        if (!toMemberId || !amount) {
          Swal.showValidationMessage("Member ID and amount are required");
          return false;
        }
        return { toMemberId, amount };
      },
    });
    if (!value) return;

    try {
      await apiRequest("/wallet/transfer", "POST", {
        fromMemberId: user.memberId,
        toMemberId: value.toMemberId,
        amount: value.amount,
      });
      await Swal.fire({
        title: "Transferred",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
      await fetchUser();
      await loadHistory(historyPagination.page);
    } catch (err: any) {
      Swal.fire("Transfer failed", err.message || "Could not transfer", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading User Profile…</h2>
      </div>
    );
  }

  if (!user) return null;

  const isActiveAccount = user.isActive !== false;
  const currentClub = clubLevel(user.club ?? wallet?.club);
  const paused = wallet?.progress?.status === "paused";
  const writtenClub = saleUser?.club != null ? clubLabel(saleUser.club) : "—";
  const clubSales = wallet?.progress?.salesDone ?? 0;
  const upcoming = wallet?.progress?.upcoming;
  const clubTarget = wallet?.progress?.salesTarget ?? 3;
  const clubPercent = Math.min(100, Math.max(0, wallet?.progress?.percent ?? 0));

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/users" className="btn-sm btn-outline back-link">
            ← Back to Users
          </Link>
          <div className="page-eyebrow">Customer Profile</div>
          <h1 className="page-title">{user.name}</h1>
          <div className="page-sub">
            {user.email}
            {user.phone ? ` · ${user.phone}` : ""}
            {" · "}Joined {formatDate(user.createdAt)}
          </div>
        </div>
        <div className="page-hdr-right">
          <span
            className={`status-badge ${isActiveAccount ? "success" : "danger"}`}
          >
            {isActiveAccount ? "Active" : "Inactive"}
          </span>
          {canEditUser && !isEditing && (
            <>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={() => void handleTransfer()}
              >
                Transfer eWallet
              </button>
              <button
                type="button"
                className="btn-sm btn-primary"
                onClick={() => setIsEditing(true)}
              >
                Edit User
              </button>
            </>
          )}
          {canEditUser && isEditing && (
            <>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="user-edit-form"
                className="btn-sm btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
          
        </div>
      </div>

      <section className="table-card user-overview-card">
        <div className="user-overview-top">
          <div className="user-overview-person">
            <AppImage
              src={getProfileImage(user)}
              alt={user.name}
              crossOrigin="anonymous"
              className="user-avatar"
            />
            <div className="user-overview-person-copy">
              <h2>{user.name}</h2>
              <p>{user.email}</p>
              <p>{user.phone || "No phone"}</p>
              <div className="profile-status-row">
                <span className={`status-badge ${isActiveAccount ? "success" : "danger"}`}>
                  {isActiveAccount ? "Active" : "Inactive"}
                </span>
                <span className="status-badge muted">
                  {registerFromLabel(user.registerFrom)}
                </span>
              </div>
            </div>
          </div>

          <dl className="user-overview-dl">
            {[
              {
                label: "Member ID",
                value: user.memberId || "—",
                copy: user.memberId ? String(user.memberId) : "",
              },
              {
                label: "Password",
                value: user.passwordText || "—",
                copy: user.passwordText ? String(user.passwordText) : "",
              },
              {
                label: "Sale Member ID",
                value: saleUser?.memberId || "Pending first order",
                copy: saleUser?.memberId ? String(saleUser.memberId) : "",
              },
              { label: "Club", value: writtenClub },
              { label: "Rank", value: user.rank || "No rank" },
              {
                label: "Club Paid",
                value:
                  Array.isArray(saleUser?.clubPaid) && saleUser.clubPaid.length
                    ? saleUser.clubPaid.join(", ")
                    : "—",
              },
              {
                label: "Position",
                value: saleUser?.position ? `Leg ${saleUser.position}` : "—",
              },
              { label: "Sponsor", value: user.sponsorId || "—" },
              {
                label: "Placement",
                value: saleUser?.placementId || "—",
              },
              {
                label: "Phone",
                value: user.phone,
                copy: String(user.phone),
              },
              {
                label: "Last login",
                value: user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never",
              },
              { label: "Joined", value: formatDate(user.createdAt) },
              { label: "Addresses", value: `${addresses.length} saved` },
            ].map((row) => (
              <div key={row.label} className="user-overview-row">
                <dt>{row.label}</dt>
                <dd>
                  <strong>{row.value}</strong>
                  {row.copy ? (
                    <button
                      type="button"
                      className="id-copy-btn"
                      onClick={() => void copyValue(row.label, row.copy)}
                    >
                      Copy
                    </button>
                  ) : null}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <section className="detail-metrics-row metrics-grid">
          {[
            {
              label: "Total Orders",
              value: stats.totalOrders ?? 0,
              icon: "📦",
              tone: "blue",
              helper: "All orders placed by this customer",
            },
            {
              label: "Total Spent",
              value: formatCurrency(stats.totalSpent ?? 0),
              icon: "💰",
              tone: "green",
              helper: "Excludes cancelled and refunded",
            },
            {
              label: "Wishlist Items",
              value: stats.wishlistCount ?? 0,
              icon: "❤️",
              tone: "pink",
              helper: "Saved products",
            },
          ].map((s) => (
            <MetricCard key={s.label} metric={s} />
          ))}
        </section>
      </section>

      {isEditing && canEditUser && (
        <form
          id="user-edit-form"
          onSubmit={handleSaveUser}
          className="table-card"
        >
          <div className="table-head">
            <h2>Edit Customer Details</h2>
            <p>Update name, email, phone, password and account status</p>
          </div>
          <div className="admin-card-body admin-form-grid">
            <div className="form-field">
              <label className="modal-label">Full Name</label>
              <input
                className="admin-input"
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Email Address</label>
              <input
                type="email"
                className="admin-input"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Phone Number</label>
              <input
                className="admin-input"
                value={form.phone}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
                placeholder="Optional"
              />
            </div>
            <div className="form-field">
              <label className="modal-label">Password</label>
              <input
                className="admin-input"
                value={form.password}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    password: event.target.value,
                  }))
                }
                placeholder="Leave blank to keep current password"
                minLength={6}
              />
            </div>
            <div className="form-field admin-status-field">
              <ToggleSwitch
                variant="row"
                label="Active account"
                hint="Customer can sign in and place orders"
                checked={form.isActive}
                onChange={(isActive) =>
                  setForm((current) => ({ ...current, isActive }))
                }
              />
            </div>
          </div>
        </form>
      )}

      <section className="table-card wallet-club-card">
        <div className="table-head">
          <h2>Wallet</h2>
          <p>
            Member funds
            {user.memberId ? ` · ${user.memberId}` : ""}
          </p>
        </div>

        <section className="metrics-grid wallet-fund-grid">
          {[
            {
              label: "eWallet",
              value: formatCurrency(wallet?.eWallet ?? user.eWallet ?? 0),
              icon: "💳",
              tone: "green",
              helper: "Withdraw and transfer",
            },
            {
              label: "Re-purchases",
              value: formatCurrency(wallet?.repurchaseFund ?? user.repurchaseFund ?? 0),
              icon: "🔁",
              tone: "orange",
              helper: "Spend on orders only",
            },
            {
              label: "Working Fund",
              value: formatCurrency(wallet?.workingFund ?? user.workingFund ?? 0),
              icon: "⚡",
              tone: "green",
              helper: "In eWallet · own placement-stage income",
            },
            {
              label: "Up-line Fund",
              value: formatCurrency(wallet?.uplineFund ?? user.uplineFund ?? 0),
              icon: "⬆️",
              tone: "green",
              helper: "In eWallet · from direct sponsored members",
            },
            {
              label: "Upgrade Fund",
              value: formatCurrency(wallet?.upgradeFund ?? user.upgradeFund ?? 0),
              icon: "🔒",
              tone: "blue",
              helper: wallet?.progress?.isCompany ? "Member upgrade transfers received"
                : wallet?.progress?.upgradeRecipientId ? `Stage allocation sent to ${wallet.progress.upgradeRecipientId}; incoming transfers stay here`
                : "Stage allocation goes two placement levels up; Company fallback",
            },
            {
              label: "Profit Fund",
              value: formatCurrency(wallet?.profitFund ?? user.profitFund ?? 0),
              icon: "🏦",
              tone: "orange",
              helper: "In eWallet · Leader → Royalty",
            },
          ].map((metric) => (
            <MetricCard key={metric.label} metric={metric} />
          ))}
        </section>
      </section>

      <section className="table-card wallet-club-card club-progress-card">
        <div className="table-head">
          <h2>Club Progress</h2>
          <p>
            {wallet?.progress?.isCompany
              ? "Plan amounts shown below; company income comes from eligible member upgrade transfers, except the initial Club 1 payout. See history for actual credits."
              : "Working and Re-purchases: member · Upgrade: second placement ancestor (Company fallback) · Up-line: direct sponsor"}
          </p>
          <p className="club-progress-sub">
            The two-stage flow repeats in Clubs 1–6. Leader–Royalty use monthly company profit-share requirements; rank follows the highest monthly target met, or becomes inactive if none qualify.
          </p>
        </div>

        <div className="wallet-hero">
          <div className="wallet-hero-club">
            <div className="club-kicker">Current club</div>
            <h3>{writtenClub}</h3>
            <p>Current rank: <strong>{user.rank || "No rank"}</strong>{ranks?.month ? ` · ${ranks.month}` : ""}</p>
            <p>
              {paused ? "Payouts and progression are paused for this club."
                : upcoming ? `${clubSales} of ${clubTarget} ${clubTarget === 9 ? "complete groups of 3" : "placement sales"} · ${upcoming.remaining} more to complete this stage`
                : wallet?.progress?.allComplete ? "All 6 numbered clubs complete" : "Progress unavailable; reload user details"}
            </p>
            {wallet?.progress && <div className="wallet-progress-track">
              <div
                className="wallet-progress-fill"
                style={{ width: `${clubPercent}%` }}
              />
            </div>}
          </div>
          <div className="wallet-hero-stats">
            <div className="wallet-hero-stat">
              <span>Lifetime sponsored</span>
              <strong>{wallet?.sponsoredCount ?? 0}</strong>
            </div>
            <div className="wallet-hero-stat">
              <span>{clubTarget === 9 ? "Complete groups of 3" : "This club sales"}</span>
              <strong>{clubSales}</strong>
            </div>
          </div>
        </div>

        <ClubProgressBoard progress={wallet?.progress} ranks={ranks} currentClub={currentClub} />
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>History ({historyPagination.total})</h2>
          <p>Credit and debit passbook for this member</p>
        </div>
        <form
          className="toolbar" style={{borderRadius: "0px"}}
          onSubmit={(event) => {
            event.preventDefault();
            void loadHistory(1);
          }}
        >
          <select
            className="filter-select"
            value={historyFilter.type}
            onChange={(event) =>
              setHistoryFilter((current) => ({ ...current, type: event.target.value }))
            }
          >
            <option value="all">All types</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>
          <select
            className="filter-select"
            value={historyFilter.fund}
            onChange={(event) =>
              setHistoryFilter((current) => ({ ...current, fund: event.target.value }))
            }
          >
            {HISTORY_FUNDS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <label className="filter-date-wrap">
            <input
              type="date"
              className="filter-select filter-date"
              value={historyFilter.from}
              onChange={(event) =>
                setHistoryFilter((current) => ({ ...current, from: event.target.value }))
              }
            />
          </label>
          <label className="filter-date-wrap">
            <input
              type="date"
              className="filter-select filter-date"
              value={historyFilter.to}
              onChange={(event) =>
                setHistoryFilter((current) => ({ ...current, to: event.target.value }))
              }
            />
          </label>
          <ResetButton
            onClick={() => {
              setHistoryFilter({ type: "all", fund: "all", from: "", to: "" });
            }}
          />
        </form>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Fund</th>
                <th>History</th>
                <th>Amount</th>
                <th>Fund after</th>
                <th>eWallet after</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    Loading history...
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state">
                    No history for these filters.
                  </td>
                </tr>
              ) : (
                history.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <span
                        className={`status-badge ${
                          row.direction === "credit" ? "success" : "danger"
                        }`}
                      >
                        {row.direction === "credit" ? "Credit" : "Debit"}
                      </span>
                    </td>
                    <td>{row.fundLabel || row.fund}</td>
                    <td>
                      <HistoryDetails row={row} />
                    </td>
                    <td>
                      {row.direction === "debit" ? "−" : "+"}
                      {formatCurrency(row.amount)}
                    </td>
                    <td>{formatCurrency(row.balanceAfter)}</td>
                    <td>
                      {row.eWalletAfter == null ? "—" : formatCurrency(row.eWalletAfter)}
                    </td>
                    <td>{formatDate(row.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar
          page={historyPagination.page}
          limit={historyPagination.limit}
          total={historyPagination.total}
          totalPages={historyPagination.totalPages}
          itemLabel="entries"
          onPageChange={(page) => void loadHistory(page)}
          onLimitChange={(limit) =>
            setHistoryPagination((current) => ({ ...current, limit, page: 1 }))
          }
        />
      </section>

      <section className="table-card mlm-tree-panel">
        <div className="table-head">
          <div>
            <h2>Network Tree</h2>
            <p>
              3-leg downline · {treeTotal} member{treeTotal === 1 ? "" : "s"}
              {saleUser?.memberId && (
                <>
                  {" · Sale ID "}
                  {saleUser.memberId}
                  {" · "}
                  <Link to={`/users/tree?memberId=${saleUser.memberId}`}>
                    Open full tree page
                  </Link>
                </>
              )}
            </p>
          </div>
        </div>
        {tree ? (
          <Tree node={tree} compact defaultExpandDepth={1} />
        ) : !saleUser ? (
          <div className="empty-state">
            Sale network ID is created after the first paid or delivered order.
          </div>
        ) : children.length === 0 ? (
          <div className="empty-state">No downline yet.</div>
        ) : (
          <div className="empty-state">Unable to load tree.</div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Recent Orders ({recentOrders.length})</h2>
          <p>Last 5 orders placed by this customer</p>
        </div>

        {recentOrders.length === 0 ? (
          <div className="empty-state">No orders placed yet.</div>
        ) : (
          <div className="orders-table-scroll">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Items</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order: any) => (
                  <tr key={order._id}>
                    <td>
                      <Link to={`/orders/${order._id}`} className="order-code">
                        {order.orderCode ||
                          `#${String(order._id).slice(-8).toUpperCase()}`}
                      </Link>
                    </td>
                    <td>
                      {order.itemsCount} item{order.itemsCount !== 1 ? "s" : ""}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${order.paymentStatus === "Completed" ? "success" : "warning"}`}
                      >
                        {order.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <strong>{formatCurrency(order.totalPrice)}</strong>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${orderStatusTone(order.orderStatus)}`}
                      >
                        {order.orderStatus}
                      </span>
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <Link
                        to={`/orders/${order._id}`}
                        className="btn-sm btn-outline"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="table-card">
        <div className="table-head">
          <h2>Saved Addresses ({addresses.length})</h2>
          <p>Delivery addresses saved by this customer</p>
        </div>
        {addresses.length === 0 ? (
          <div className="empty-state">No saved addresses yet.</div>
        ) : (
          <div className="address-grid">
            {addresses.map((addr: any, i: number) => (
              <div
                key={addr._id || i}
                className={`address-card ${addr.isDefault ? "is-default" : ""}`}
              >
                {addr.isDefault && (
                  <span className="address-default-badge">Default</span>
                )}
                <div className="address-label">{addr.label || "home"}</div>
                <div>{addr.fullName}</div>
                <div>{addr.address}</div>
                <div>
                  {addr.city}, {addr.state} — {addr.pinCode}
                </div>
                <div>{addr.country}</div>
                <div className="text-muted-sm">{addr.phoneNo}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default UserDetail;
